import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, Transition as _Transition } from "vue";
const _hoisted_1 = { key: 0 };
const _hoisted_2 = { class: "submitControls" };
const _hoisted_3 = { class: "centered" };
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAppState } from '@/composables/useAppState';
import { useAuth } from '@/composables/useAuth';
import { useCustomer } from '@/composables/useCustomer';
import { useQuestionnaire } from '@/composables/useQuestionnaire';
import CustomerLoadWait from './elements/CustomerLoadWait.vue';
import HeaderBlock from './elements/HeaderBlock.vue';
import MGButton from './elements/MGButton.vue';
import QuestionnaireQuestion from './elements/QuestionnaireQuestion.vue';
export default /*@__PURE__*/ _defineComponent({
    __name: 'QuestionnairePage',
    setup(__props) {
        const route = useRoute();
        const router = useRouter();
        const state = useAppState();
        useAuth();
        const { getCustomer } = useCustomer();
        const { getQuestionnaireByGuidOrKey, getQuestionnaireAnswers, saveQuestionnaireAnswer } = useQuestionnaire();
        const currentQuestionId = ref(null);
        const currentQuestionNumber = ref(0);
        const finalQuestionId = ref(null);
        const allQuestionIds = ref([]);
        const haveAnswer = ref(false);
        const allPageQuestionsAnswered = ref(false);
        const answersRetrieved = ref(false);
        const breadcrumb = ref([]);
        const theme = ref('dark');
        const allowBack = ref(false);
        const allowSkip = ref(true);
        const nextLabel = ref('Continue');
        const returnRoute = ref('/money');
        const progressValue = computed(() => {
            if (allQuestionIds.value.length === 0)
                return 0;
            return ((currentQuestionNumber.value + 1) / allQuestionIds.value.length) * 100;
        });
        function themeBackground(t) {
            if (t === 'dark')
                return '#332847';
            return t === 'onboarding' ? 'var(--core-neutral)' : 'white';
        }
        const backgroundColor = computed(() => {
            if (route.meta.backgroundColor) {
                return route.meta.backgroundColor;
            }
            return themeBackground(route.query.theme || theme.value);
        });
        /* Each question must earn its own Continue — the newly mounted question re-emits any saved answer */
        watch(currentQuestionId, () => {
            allPageQuestionsAnswered.value = false;
        });
        function getQuestionnaire(followupQuestionnaireId) {
            const id = followupQuestionnaireId || route.params.id;
            getQuestionnaireByGuidOrKey(id, () => {
                const byOrder = (a, b) => (a.order || 0) - (b.order || 0);
                state.questionnaire.questionnaireQuestions.sort(byOrder);
                state.questionnaire.questionnaireQuestions.forEach((q) => q.question.possibleAnswers?.sort(byOrder));
                currentQuestionId.value = state.questionnaire.questionnaireQuestions[currentQuestionNumber.value].questionId;
                allQuestionIds.value = state.questionnaire.questionnaireQuestions.map((q) => q.questionId);
                finalQuestionId.value = allQuestionIds.value[allQuestionIds.value.length - 1];
                if (route.query.returnRoute) {
                    returnRoute.value = route.query.returnRoute;
                }
                if (state.questionnaire.returnUrl) {
                    returnRoute.value = state.questionnaire.returnUrl;
                }
                selectAlreadyAnsweredSelections(id);
            });
        }
        function answered(userAnswer) {
            state.questionnaire.questionnaireQuestions[currentQuestionNumber.value].unsavedAnswer = userAnswer;
            const question = state.questionnaire.questionnaireQuestions[currentQuestionNumber.value].question;
            /* Multiple choice needs at least minAmount selections before Continue enables */
            const minSelections = question.multipleChoice ? (question.minAmount || 0) : 0;
            allPageQuestionsAnswered.value = userAnswer.length >= minSelections;
        }
        function commitAnswers() {
            const question = state.questionnaire.questionnaireQuestions[currentQuestionNumber.value];
            question.question.selectedAnswer = question.unsavedAnswer;
            saveQuestionnaireAnswer(question, (result) => {
                nextQuestion(result);
            });
        }
        function nextQuestion(lastResult = "") {
            determineNextQuestion(lastResult);
        }
        function goBack() {
            const breadCrumb = breadcrumb.value.pop();
            currentQuestionId.value = breadCrumb.questionId;
            currentQuestionNumber.value = breadCrumb.questionNumber;
        }
        function determineNextQuestion(lastResult = "") {
            breadcrumb.value.push({ questionId: currentQuestionId.value, questionNumber: currentQuestionNumber.value });
            currentQuestionNumber.value++;
            currentQuestionId.value = allQuestionIds.value[currentQuestionNumber.value];
            if (currentQuestionNumber.value >= allQuestionIds.value.length) {
                router.push({ path: returnRoute.value, query: { lastResult: lastResult } });
                return;
            }
            const conditionalQuestionKey = state.questionnaire.questionnaireQuestions[currentQuestionNumber.value].conditionalQuestionKey;
            const conditionalAnswerValue = state.questionnaire.questionnaireQuestions[currentQuestionNumber.value].conditionalAnswerValue;
            if (!conditionalQuestionKey)
                return;
            const conditionalQuestion = state.questionnaire.questionnaireQuestions.find((q) => q.question.key === conditionalQuestionKey);
            let skipThis = false;
            if (conditionalQuestion &&
                (!conditionalQuestion.question.selectedAnswer ||
                    conditionalQuestion.question.selectedAnswer.length === 0)) {
                skipThis = true;
            }
            if (conditionalQuestion &&
                (conditionalQuestion.question.selectedAnswer &&
                    conditionalQuestion.question.selectedAnswer[0] &&
                    conditionalQuestion.question.selectedAnswer[0].answer.value !== conditionalAnswerValue)) {
                skipThis = true;
            }
            if (skipThis) {
                currentQuestionNumber.value++;
                currentQuestionId.value = allQuestionIds.value[currentQuestionNumber.value];
                if (currentQuestionNumber.value >= allQuestionIds.value.length) {
                    router.replace({ path: returnRoute.value });
                    return;
                }
            }
        }
        function selectAlreadyAnsweredSelections(id) {
            getQuestionnaireAnswers(id, (answers) => {
                answers = answers.filter((a) => a.isLatest);
                answers.forEach((answer) => {
                    const question = state.questionnaire.questionnaireQuestions.find((q) => q.question.id === answer.question.id);
                    if (question) {
                        if (answer.answer) {
                            const selectedAnswer = question.question.possibleAnswers.find((a) => a.answerId === answer.answer.id);
                            if (selectedAnswer) {
                                selectedAnswer.selected = true;
                                if (!question.question.selectedAnswer) {
                                    question.question.selectedAnswer = [];
                                }
                                question.question.selectedAnswer.push(selectedAnswer);
                                question.question.isDefaultValue = true;
                            }
                        }
                        else {
                            const freeAmountQuestion = state.questionnaire.questionnaireQuestions.find((q) => q.question.id === answer.question.id);
                            if (freeAmountQuestion) {
                                freeAmountQuestion.question.selectedAnswer = answer.freeAnswer;
                                freeAmountQuestion.question.isDefaultValue = true;
                            }
                        }
                    }
                });
                answersRetrieved.value = true;
            });
        }
        function onLoad() {
            getCustomer();
            getQuestionnaire();
            /* No theme param (deep links): App.vue's body falls back to the light background, so match it */
            theme.value = route.query.theme || 'light';
            allowBack.value = route.query.allowBack == 'true';
            if (route.query.allowSkip == 'false') {
                allowSkip.value = false;
            }
            route.meta.backgroundColor = themeBackground(theme.value);
            nextLabel.value = route.query.nextLabel || 'Continue';
        }
        onMounted(() => {
            onLoad();
        });
        return (_ctx, _cache) => {
            const _component_v_progress_linear = _resolveComponent("v-progress-linear");
            const _component_v_btn = _resolveComponent("v-btn");
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(HeaderBlock, {
                    title: theme.value == 'onboarding' ? _unref(state).questionnaire?.title : '',
                    bgcolor: backgroundColor.value,
                    color: (theme.value == 'dark' ? 'white' : theme.value == 'onboarding' ? 'var(--background-color-primary)' : '#281C3B'),
                    border: false,
                    "back-button": theme.value == 'onboarding',
                    onBack: _cache[0] || (_cache[0] = ($event) => (breadcrumb.value.length ? goBack() : _unref(router).back()))
                }, {
                    default: _withCtx(() => [
                        _createElementVNode("div", {
                            class: _normalizeClass('progress-container ' + theme.value)
                        }, [
                            _createVNode(_component_v_progress_linear, {
                                "model-value": progressValue.value,
                                rounded: theme.value != 'onboarding',
                                height: theme.value == 'onboarding' ? 3 : 4
                            }, null, 8, ["model-value", "rounded", "height"])
                        ], 2)
                    ]),
                    _: 1
                }, 8, ["title", "bgcolor", "color", "back-button"]),
                _createVNode(CustomerLoadWait, {
                    customer: _unref(state).questionnaire
                }, null, 8, ["customer"]),
                _createVNode(_Transition, null, {
                    default: _withCtx(() => [
                        _createElementVNode("div", {
                            class: _normalizeClass('questionnaire ' + theme.value)
                        }, [
                            (_unref(state).questionnaire && answersRetrieved.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(state).questionnaire.questionnaireQuestions, (q) => {
                                        return (_openBlock(), _createElementBlock("div", {
                                            key: q.id
                                        }, [
                                            (currentQuestionId.value == q.question.id)
                                                ? (_openBlock(), _createBlock(QuestionnaireQuestion, {
                                                    key: 0,
                                                    theme: theme.value,
                                                    formatter: _unref(state).formatter,
                                                    question: q.question,
                                                    onAnswered: _cache[1] || (_cache[1] = (a) => { answered(a); })
                                                }, null, 8, ["theme", "formatter", "question"]))
                                                : _createCommentVNode("", true)
                                        ]));
                                    }), 128))
                                ]))
                                : _createCommentVNode("", true),
                            _createElementVNode("div", _hoisted_2, [
                                _createVNode(MGButton, {
                                    alt: theme.value == 'dark',
                                    class: "mt-5",
                                    arrow: true,
                                    disabled: !allPageQuestionsAnswered.value,
                                    onClick: _cache[2] || (_cache[2] = ($event) => (commitAnswers())),
                                    text: nextLabel.value
                                }, null, 8, ["alt", "disabled", "text"]),
                                _createElementVNode("div", _hoisted_3, [
                                    (allowBack.value && breadcrumb.value.length > 0 && theme.value != 'onboarding')
                                        ? (_openBlock(), _createBlock(_component_v_btn, {
                                            key: 0,
                                            class: "link mt-5",
                                            variant: "text",
                                            outline: true,
                                            onClick: _cache[3] || (_cache[3] = ($event) => (goBack())),
                                            text: "Back"
                                        }))
                                        : _createCommentVNode("", true),
                                    (allowSkip.value)
                                        ? (_openBlock(), _createBlock(_component_v_btn, {
                                            key: 1,
                                            class: "link mt-5",
                                            variant: "text",
                                            onClick: _cache[4] || (_cache[4] = ($event) => (nextQuestion()))
                                        }, {
                                            default: _withCtx(() => _cache[5] || (_cache[5] = [
                                                _createTextVNode("Skip")
                                            ])),
                                            _: 1
                                        }))
                                        : _createCommentVNode("", true)
                                ])
                            ])
                        ], 2)
                    ]),
                    _: 1
                })
            ], 64));
        };
    }
});
